'use client';
import { useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';
import ChargebeeIcon from '@components/_components/_assets/chargebee-icon.svg';
import { useExplorer } from '@/hooks/useExplorer';
import Login from '@components/ui/Auth/Login';
import { useFetchUser } from '@/auth/hooks';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Search } from '@components/_components/ui/Search';
import type { SearchItem } from '@/lib/search-data';
import { cn } from '@components/lib/utils';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { useUnsavedChanges } from '@/context/unsavedChanges';

const Header = ({ searchData }: { searchData: SearchItem[] }) => {
  const {
    apiKey,
    setApiKey,
    setSiteId,
    setSiteName,
    siteId,
    siteName,
    setEndpointAttributes,
    setUserPrompt,
  } = useExplorer();
  const { clearConversation, resetToHomepage } = useCodeGenerationStore();
  const { loading: authLoading, isLoggedIn } = useFetchUser();
  const { confirmAction } = useUnsavedChanges();
  const pathname = usePathname();
  const router = useRouter();

  // Clear conversation when the user becomes logged out (auto-logout,
  // token expiry, cross-tab logout, SSO cookie loss) to prevent stale
  // session IDs from being reused against a different tenant host.
  const prevIsLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    if (prevIsLoggedInRef.current && !isLoggedIn) {
      clearConversation();
    }
    prevIsLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, clearConversation]);

  // Simplified loading logic:
  // - If authLoading is true: always show loader (covers login, logout, any auth operation)
  // - If authLoading is false AND user is logged in: wait for siteId/siteName
  // - If user is not logged in: don't wait for siteId/siteName
  const isLoading = authLoading || (isLoggedIn && (!siteId || !siteName));

  const handleApiKeyChangeEvent = useCallback(
    (value: string, newSiteId?: string, newSiteName?: string) => {
      // If the site is actually changing, clear the conversation to avoid
      // sending session requests to a mismatched tenant host
      if (newSiteName && newSiteName !== siteName) {
        clearConversation();
      }
      setApiKey(value);
      if (newSiteId) setSiteId(newSiteId);
      if (newSiteName) setSiteName(newSiteName);
    },
    [setApiKey, setSiteId, setSiteName, siteName, clearConversation]
  );

  const isExploreActive = pathname === '/';
  const isPlaygroundActive =
    !isExploreActive &&
    !pathname.startsWith('/events') &&
    pathname.split('/').filter(Boolean).length >= 2;
  const isEventsActive = pathname.startsWith('/events');

  // Clear only the state relevant to the tab currently being left.
  // Explore Endpoints owns AI chat state; API Playground owns form state.
  const clearCurrentTabState = () => {
    if (isExploreActive) {
      resetToHomepage();
      setUserPrompt('');
    } else if (isPlaygroundActive) {
      setEndpointAttributes(undefined);
    }
    // Events tab has no persistent state — nothing to clear.
  };

  const handleExploreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    confirmAction(() => {
      clearCurrentTabState();
      router.push('/');
    });
  };

  const handlePlaygroundClick = (e: React.MouseEvent) => {
    e.preventDefault();
    confirmAction(() => {
      clearCurrentTabState();
      router.push('/customers/create_a_customer');
    });
  };

  const handleEventsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    confirmAction(() => {
      clearCurrentTabState();
      router.push('/events');
    });
  };

  return (
    <div className="sticky top-0 z-50 backdrop-blur bg-opacity-40 flex items-center justify-between px-4 py-2 pb-0 isolate bg-[linear-gradient(270deg,_rgba(211,_217,_220,_0.01)_21.38%,_#ffffff_88.33%)] shadow-[0px_4px_4px_0px_#0000000d] border-b border-gray-200">
      {/* Left: Logo + Navigation tabs */}
      <div className="flex items-center gap-6">
        <div className="font-sora self-center">
          <Link
            href="/"
            className="flex gap-2 text-[1rem] leading-none font-semibold"
          >
            <Image src={ChargebeeIcon} alt="Chargebee" width={28} height={28} />
          </Link>
        </div>
        <nav className="flex items-end gap-4 self-stretch">
          <Link
            href="/"
            onClick={handleExploreClick}
            className={cn(
              'relative px-1 pt-3 pb-4 text-sm transition-colors',
              isExploreActive
                ? 'text-gray-900 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Explore Endpoints
            {isExploreActive && (
              <span className="absolute bottom-2 left-0 right-0 h-[2px] rounded-full bg-gray-900" />
            )}
          </Link>
          <Link
            href="/customers/create_a_customer"
            onClick={handlePlaygroundClick}
            className={cn(
              'relative px-1 pt-3 pb-4 text-sm transition-colors',
              isPlaygroundActive
                ? 'text-gray-900 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            API Playground
            {isPlaygroundActive && (
              <span className="absolute bottom-2 left-0 right-0 h-[2px] rounded-full bg-gray-900" />
            )}
          </Link>
          <Link
            href="/events"
            onClick={handleEventsClick}
            className={cn(
              'relative px-1 pt-3 pb-4 text-sm transition-colors',
              isEventsActive
                ? 'text-gray-900 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Events
            {isEventsActive && (
              <span className="absolute bottom-2 left-0 right-0 h-[2px] rounded-full bg-gray-900" />
            )}
          </Link>
        </nav>
      </div>

      {/* Hidden: Search (keeps Cmd+K shortcut working) */}
      <div className="hidden">
        <Search searchData={searchData} />
      </div>

      {/* Right: Login / Site selector */}
      <div className="flex gap-2 font-normal items-center">
        <Login
          key={apiKey}
          onSave={handleApiKeyChangeEvent}
          value={apiKey}
          isLoading={isLoading}
          options={{
            label: 'API Key',
            disable: false,
          }}
        />
      </div>
    </div>
  );
};

export default Header;
